/** @jsxImportSource @emotion/react */
import { useContext, useEffect, useState } from "react";
import MarkEmailUnreadOutlinedIcon from "@mui/icons-material/MarkEmailUnreadOutlined";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import { Skeleton } from "@mui/material";
import moment from "moment";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import AppBar from "../../components/AppBar";
import Footer1 from "../../components/Footer/Footer";
import Pagination from "../../components/Pagination";
import { UserContext } from "../../context/UserContext";
import { useGetNavItems } from "../Home/appbarNav";
import { useGetRewardsScreenStyles } from "../Rewards/index.styles";
import { Notificationconsumer } from "../../hooks/services/notifications";
import { baseUrl } from "../../connections";
import t from "../../theme";

const PAGE_SIZE = 10;

const Notifications = () => {
  const navigate = useNavigate();
  const classes = useGetRewardsScreenStyles();
  const { userDetails } = useContext(UserContext);
  const [pageNumber, setPageNumber] = useState(1);

  const {
    noticationdata,
    isLoading,
    count,
    setPayloadsdata,
    setpage,
  } = Notificationconsumer();

  useEffect(() => {
    if (userDetails?.uuid) setPayloadsdata(userDetails.uuid);
  }, [userDetails?.uuid]);

  useEffect(() => {
    setpage(`${pageNumber - 1}`);
  }, [pageNumber]);

  const markSeen = (notifyUuid) => {
    axios
      .put(
        `${baseUrl}/notification/update/seen`,
        { notify_uuid: notifyUuid },
        { headers: { "x-auth-token": userDetails?.token } },
      )
      .catch(() => {});
  };

  const onNotificationClick = (item) => {
    markSeen(item?.uuid);
    if (item?.link) navigate(item.link);
  };

  return (
    <div css={classes.pageContainer}>
      <AppBar navItems={useGetNavItems({}).navItems} pageTitle="Notifications" />

      <div
        css={{
          width: "min(720px, 100%)",
          margin: "0 auto",
          padding: "20px 16px 40px",
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        {isLoading &&
          Array.from({ length: 5 }).map((_, i) => (
            <Skeleton
              key={i}
              variant="rounded"
              height={76}
              sx={{ borderRadius: "14px", bgcolor: "rgba(0,0,0,0.06)" }}
            />
          ))}

        {!isLoading && (!noticationdata || noticationdata.length === 0) && (
          <div
            css={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 10,
              padding: "48px 20px",
              textAlign: "center",
            }}
          >
            <div
              css={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                background: t.green.bg,
                border: `1px solid ${t.green.border}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: t.green.solid,
              }}
            >
              <NotificationsNoneOutlinedIcon sx={{ fontSize: "1.5rem" }} />
            </div>
            <p css={{ margin: 0, fontWeight: 600, color: t.text.primary }}>
              No notifications yet
            </p>
            <p css={{ margin: 0, fontSize: "0.85rem", color: t.text.muted }}>
              We&rsquo;ll let you know here when something needs your
              attention.
            </p>
          </div>
        )}

        {!isLoading &&
          noticationdata?.map((item) => {
            const isUnseen = item?.is_seen !== true;
            return (
              <button
                key={item?.uuid}
                type="button"
                onClick={() => onNotificationClick(item)}
                css={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 12,
                  width: "100%",
                  textAlign: "left",
                  cursor: "pointer",
                  borderRadius: "14px",
                  padding: "14px 16px",
                  background: isUnseen ? t.glass.light : "rgba(255,255,255,0.22)",
                  backdropFilter: t.blur.light,
                  WebkitBackdropFilter: t.blur.light,
                  border: isUnseen
                    ? `1px solid ${t.green.border}`
                    : t.border.subtle,
                  boxShadow: t.shadow.xs,
                  transition: t.transition.fast,
                  "&:hover": { background: t.green.bgHover },
                  "&:active": { transform: "scale(0.99)" },
                }}
              >
                <div
                  css={{
                    width: 36,
                    height: 36,
                    borderRadius: "50%",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: isUnseen ? t.green.bg : "rgba(0,0,0,0.04)",
                    color: isUnseen ? t.green.solid : t.text.muted,
                  }}
                >
                  <MarkEmailUnreadOutlinedIcon sx={{ fontSize: "1.1rem" }} />
                </div>
                <div css={{ flex: 1, minWidth: 0 }}>
                  <div
                    css={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 2,
                    }}
                  >
                    {isUnseen && (
                      <span
                        aria-hidden="true"
                        css={{
                          width: 7,
                          height: 7,
                          borderRadius: "50%",
                          background: t.green.solid,
                          flexShrink: 0,
                        }}
                      />
                    )}
                    <span
                      css={{
                        fontWeight: isUnseen ? 700 : 500,
                        color: t.text.primary,
                        fontSize: "0.92rem",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item?.title}
                    </span>
                  </div>
                  <p
                    css={{
                      margin: 0,
                      fontSize: "0.82rem",
                      color: t.text.secondary,
                      lineHeight: 1.4,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    {item?.message}
                  </p>
                  <span
                    css={{ fontSize: "0.72rem", color: t.text.muted }}
                  >
                    {item?.createdAt
                      ? moment(item.createdAt).format("MMM D, YYYY")
                      : ""}
                  </span>
                </div>
              </button>
            );
          })}

        {!isLoading && count > PAGE_SIZE && (
          <div css={{ display: "flex", justifyContent: "center", marginTop: 8 }}>
            <Pagination
              currentPage={pageNumber}
              totalCount={count}
              pageSize={PAGE_SIZE}
              onPageChange={(page) => setPageNumber(page)}
            />
          </div>
        )}
      </div>

      <Footer1 />
    </div>
  );
};

export default Notifications;
